import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { rebuildCode, rebuildJobCode } from 'frontend/core/src/data/apis/api';
import { useRestMutation } from 'frontend/core/src/data/util';
import { useMemo, useState } from 'react';

import { QueryKeys } from '../../queries/common';
import { Entity } from '../../types/Entity';
import { useProjectState } from '../Project/hooks';
import { UPDATE_CANCEL } from './constants';
import { useProjectEntityCodeGenStatus } from './useProjectEntityCodeGenStatus';
import { getCodeGenWarningDialog } from './util';

type RebuildParamsType = {
  saveBtn?: string;
  title?: string;
  codeGenWarning?: string;
  handleCallback: () => Promise<unknown>;
  warningText?: string;
  pipelineIds?: string[];
  disabledLostText?: boolean;
};

export const useCodeGenContext = ({ isPipelineIDE }: { isPipelineIDE?: boolean }) => {
  const [hasCommitPanel, toggleCommitPanel] = useState(false);
  const queryClient = useQueryClient();
  const {
    hasProjectDataFetchEnabled,
    isLoading,
    isAdminLoading,
    isGitInfoLoading,
    unCommittedCount,
    projectId,
    currentBranch,
    isSqlProject
  } = useProjectState();
  const isRebuildEnabled = useGlobalVariables().isCodeGenerationEnabled;
  const currentProjectId = hasProjectDataFetchEnabled ? projectId : '';

  const { entities: pipelines } = useProjectEntityCodeGenStatus(
    currentProjectId,
    Boolean(currentProjectId && isRebuildEnabled),
    Entity.Pipeline
  );

  const hasRebuild = isRebuildEnabled && pipelines.length > 0;
  const hasCodeGenEnabled = Boolean(currentProjectId && !isSqlProject);
  const rebuildMutation = useRestMutation(
    (payload: { branch: string; projectId: string; pipelineIds?: string[] }) => {
      return rebuildCode(payload);
    },
    {
      retry: false,
      sentryTag: 'project.rebuildJob',
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.Git]);
        queryClient.invalidateQueries([QueryKeys.Project]);
      }
    }
  );
  const rebuildJobMutation = useRestMutation(
    (payload: { branch: string; projectId: string }) => {
      return rebuildJobCode(payload);
    },
    {
      retry: false,
      sentryTag: 'project.rebuildJobJob'
    }
  );
  const triggerRebuildCall = usePersistentCallback(
    (branch?: string, resp?: unknown, pipelineIds?: string[], shouldRebuildJob?: boolean) => {
      if (isRebuildEnabled && resp !== UPDATE_CANCEL && !isSqlProject && pipelines.length > 0) {
        rebuildMutation.mutateAsync({ projectId, branch: branch || currentBranch, pipelineIds });
      }
      if (shouldRebuildJob && isRebuildEnabled && !isSqlProject) {
        rebuildJobMutation.mutateAsync({ projectId, branch: branch || currentBranch });
      }
      return resp;
    }
  );
  const handleRebuildCode = usePersistentCallback(
    async ({
      title,
      codeGenWarning,
      saveBtn,
      handleCallback,
      warningText = '',
      pipelineIds,
      disabledLostText
    }: RebuildParamsType) => {
      if (hasRebuild || warningText) {
        const allowed = await getCodeGenWarningDialog({
          saveBtn,
          isPipelineIDE,
          codeGenWarning,
          warningText,
          title,
          disabledLostText,
          onCloseClick: () => (unCommittedCount > 0 ? toggleCommitPanel(!hasCommitPanel) : null),
          unCommittedCount: hasRebuild ? unCommittedCount : 0,
          hasRebuildWarning: Boolean(isRebuildEnabled)
        });
        if (!allowed) {
          return UPDATE_CANCEL;
        }
      }
      const resp = await handleCallback();
      triggerRebuildCall(currentBranch, resp, pipelineIds);
      return resp;
    }
  );
  const ctxIsLoading = rebuildMutation.isLoading || isLoading || isAdminLoading || isGitInfoLoading;
  const ctxIsRebuildEnabled = isRebuildEnabled && hasCodeGenEnabled;
  return useMemo(
    () => ({
      hasCommitPanel,
      isLoading: ctxIsLoading,
      isRebuildEnabled: ctxIsRebuildEnabled,
      actions: { toggleCommitPanel, handleRebuildCode, triggerRebuildCall }
    }),
    [hasCommitPanel, ctxIsLoading, ctxIsRebuildEnabled, toggleCommitPanel, triggerRebuildCall, handleRebuildCode]
  );
};
